//Users/aminb101/iuhimporter/autolistify-initial-working/frontend/src/components/ProductImport/ProductDropzone.jsx
import React, { useCallback, useState, useMemo, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import { useSnackbar } from 'notistack';
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Paper,
  LinearProgress,
  Snackbar,
  Alert,
  IconButton,
  Avatar,
  Tooltip
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import api from '../../services/api'; // Use centralized API service

const ProductDropzone = () => {
  const [files, setFiles] = useState([]);
  const [productDetails, setProductDetails] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [storeId, setStoreId] = useState('');
  const [loadingStore, setLoadingStore] = useState(true);
  const { enqueueSnackbar } = useSnackbar();

  // Preview images
  const previews = useMemo(() => {
    return files.map((file, index) => (
      <Box
        key={index}
        sx={{
          position: 'relative',
          width: 100,
          height: 100,
          margin: 1,
          '&:hover .delete-button': {
            opacity: 1,
          },
        }}
      >
        <Avatar
          src={file.preview}
          variant="rounded"
          sx={{ width: '100%', height: '100%' }}
        />
        <IconButton
          className="delete-button"
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            const newFiles = [...files];
            newFiles.splice(index, 1);
            setFiles(newFiles);
          }}
          sx={{
            position: 'absolute',
            top: 0,
            right: 0,
            backgroundColor: 'rgba(255, 0, 0, 0.7)',
            color: 'white',
            opacity: 0,
            transition: 'opacity 0.2s',
            '&:hover': {
              backgroundColor: 'red',
            },
          }}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Box>
    ));
  }, [files]);

  // Updated store ID fetching to match ProductAnalyticsPage approach
  useEffect(() => {
    const fetchStoreId = async () => {
      setLoadingStore(true);
      try {
        const storeRes = await api.get('/api/stores'); // Use api.get
        if (!storeRes.data || storeRes.data.length === 0) {
          setStoreId(null);
          setError('No stores found. Please connect your Shopify store first.');
          console.warn('No stores found.');
          return;
        }

        const currentStoreId = storeRes.data[0].id;
        setStoreId(currentStoreId);
        console.log(`Using store ID: ${currentStoreId}`);
      } catch (err) {
        console.error('Error fetching store ID:', err);
        setError('Failed to load store information. Please try again.');
        setStoreId(null);
      } finally {
        setLoadingStore(false);
      }
    };

    fetchStoreId();
  }, []);

  const onDrop = useCallback((acceptedFiles) => {
    const newFiles = acceptedFiles.map(file => Object.assign(file, {
      preview: URL.createObjectURL(file)
    }));
    setFiles(prevFiles => [...prevFiles, ...newFiles]);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.webp']
    },
    maxFiles: 10,
    multiple: true,
    noClick: files.length > 0,
    noKeyboard: files.length > 0
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);

    if (files.length === 0) {
      setError('Please select at least one image file');
      return;
    }

    if (!storeId) {
      setError('No store selected. Please select a store first.');
      return;
    }

    const formData = new FormData();

    // Append all files
    files.forEach((file, index) => {
      formData.append('images', file);
    });

    // Append other data
    if (productDetails) {
      formData.append('productDetails', productDetails);
    }
    formData.append('storeId', storeId);

    try {
      setIsLoading(true);
      setUploadProgress(0);

      console.log('Sending request to server with form data:', {
        storeId,
        fileCount: files.length,
        hasProductDetails: !!productDetails,
        fileNames: files.map(f => f.name)
      });

      const response = await api.post('/api/ui-import', formData, { // Use api.post and relative path
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'X-Requested-With': 'XMLHttpRequest'
        },
        timeout: 300000, // 5 minute timeout
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          setUploadProgress(percentCompleted);
        },
      });

      console.log('Import successful:', response.data);
      setSuccess(true);
      setFiles([]);
      setProductDetails('');
      setUploadProgress(0);

      // Show success message to user
      enqueueSnackbar('Product imported successfully!', { variant: 'success' });

    } catch (error) {
      console.error('Error importing products:', error);

      let errorMessage = 'Failed to import product. Please try again.';
      let details = '';

      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        console.error('Response data:', error.response.data);
        console.error('Response status:', error.response.status);
        console.error('Response headers:', error.response.headers);

        if (error.response.data) {
          if (error.response.data.error) {
            errorMessage = error.response.data.error;
          }
          if (error.response.data.details) {
            details = error.response.data.details;
            console.error('Error details:', details);
          }
        }

        if (error.response.status === 400) {
          errorMessage = 'Invalid request. Please check your input.';
        } else if (error.response.status === 401) {
          errorMessage = 'Authentication required. Please log in again.';
        } else if (error.response.status === 413) {
          errorMessage = 'File(s) too large. Total size must be less than 10MB.';
        } else if (error.response.status >= 500) {
          errorMessage = 'Server error. Please try again later.';
        }
      } else if (error.request) {
        errorMessage = 'No response from server. Please check your connection.';
      } else {
        errorMessage = error.message || 'An unknown error occurred.';
      }

      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setError(null);
    setSuccess(false);
  };

  // Show loading state while fetching store
  if (loadingStore) {
    return (
      <Box sx={{ maxWidth: 800, mx: 'auto', p: 3, textAlign: 'center' }}>
        <Typography variant="h6" gutterBottom>
          Loading store information...
        </Typography>
        <LinearProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', p: 3 }}>
      <Typography variant="h5" gutterBottom>
        Import Products
      </Typography>

      {!storeId && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          No store found. Please connect your Shopify store first.
        </Alert>
      )}

      <Paper
        {...getRootProps()}
        sx={{
          p: 4,
          border: '2px dashed',
          borderColor: isDragActive ? 'primary.main' : 'grey.400',
          backgroundColor: isDragActive ? 'action.hover' : 'background.paper',
          cursor: 'pointer',
          textAlign: 'center',
          transition: 'all 0.2s ease-in-out',
          minHeight: files.length > 0 ? 'auto' : 200,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          '&:hover': {
            borderColor: 'primary.main',
            backgroundColor: 'action.hover'
          }
        }}
      >
        <input {...getInputProps()} />
        {files.length === 0 ? (
          <>
            <CloudUploadIcon sx={{ fontSize: 48, mb: 2, color: 'text.secondary' }} />
            <Typography variant="h6" gutterBottom>
              {isDragActive ? 'Drop the files here' : 'Drag & drop product images here, or click to select'}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Supports JPG, JPEG, PNG, WEBP (max 10MB each, up to 10 files)
            </Typography>
          </>
        ) : (
          <Box sx={{ textAlign: 'left' }}>
            <Typography variant="subtitle1" gutterBottom>
              {files.length} {files.length === 1 ? 'image' : 'images'} selected
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', mt: 2 }}>
              {previews}
              <Tooltip title="Add more images">
                <Box
                  sx={{
                    width: 100,
                    height: 100,
                    border: '1px dashed',
                    borderColor: 'divider',
                    borderRadius: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    m: 1,
                    cursor: 'pointer',
                    '&:hover': {
                      borderColor: 'primary.main',
                      backgroundColor: 'action.hover'
                    }
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    document.querySelector('input[type="file"]').click();
                  }}
                >
                  <AddPhotoAlternateIcon color="action" />
                </Box>
              </Tooltip>
            </Box>
          </Box>
        )}
      </Paper>

      {files.length > 0 && (
        <Box sx={{ mt: 2, mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            Selected files:
          </Typography>
          <ul style={{ paddingLeft: 20, margin: '8px 0' }}>
            {files.map((file, index) => (
              <li key={index}>{file.name} ({(file.size / 1024 / 1024).toFixed(2)} MB)</li>
            ))}
          </ul>

          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Product Details (Optional):
            </Typography>
            <textarea
              value={productDetails}
              onChange={(e) => setProductDetails(e.target.value)}
              placeholder="Enter product details, specifications, or any additional information..."
              style={{
                width: '100%',
                minHeight: 100,
                padding: '8px',
                borderRadius: '4px',
                border: '1px solid #ccc',
                fontFamily: 'inherit',
                fontSize: '14px',
              }}
            />
          </Box>

          {isLoading && (
            <Box sx={{ width: '100%', mt: 2 }}>
              <LinearProgress
                variant={uploadProgress === 0 ? 'indeterminate' : 'determinate'}
                value={uploadProgress}
              />
              <Typography variant="caption" display="block" textAlign="right" mt={0.5}>
                {uploadProgress}% {uploadProgress === 100 ? 'Processing...' : 'Uploading...'}
              </Typography>
            </Box>
          )}

          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            disabled={isLoading || !storeId}
            fullWidth
            sx={{ mt: 2 }}
            startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {isLoading ? 'Importing...' : 'Import Products'}
          </Button>
        </Box>
      )}

      <Snackbar
        open={!!error || success}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={error ? 'error' : 'success'}
          sx={{ width: '100%' }}
        >
          {error || 'Products imported successfully!'}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ProductDropzone;